import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Layers, Wallet, ArrowLeftRight, Coins, CircleDollarSign, AppWindow } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const ecosystemItems = [
  {
    icon: Layers,
    title: 'Polk Chain',
    description: 'High-performance Layer 1 blockchain with PoA consensus',
    gradient: 'from-blue-500 to-cyan-400',
    span: 'md:col-span-4',
  },
  {
    icon: Wallet,
    title: 'Polk Wallet',
    description: 'Secure multi-chain wallet for all your digital assets',
    gradient: 'from-purple-500 to-pink-400',
    span: 'md:col-span-4',
  },
  {
    icon: ArrowLeftRight,
    title: 'Polk Swap',
    description: 'Decentralized exchange with deep liquidity pools',
    gradient: 'from-orange-500 to-yellow-400',
    span: 'md:col-span-4',
  },
  {
    icon: Coins,
    title: 'Polk Coin',
    description: 'Native utility token powering the ecosystem',
    gradient: 'from-green-500 to-emerald-400',
    span: 'md:col-span-6',
  },
  {
    icon: CircleDollarSign,
    title: 'PUSD',
    description: 'Algorithmic stablecoin pegged to USD',
    gradient: 'from-teal-500 to-cyan-400',
    span: 'md:col-span-6',
  },
  {
    icon: AppWindow,
    title: 'DApps',
    description: 'Thriving ecosystem of decentralized applications',
    gradient: 'from-indigo-500 to-purple-400',
    span: 'md:col-span-12',
  },
];

export const Ecosystem = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { isDark } = useTheme();

  return (
    <section
      id="ecosystem"
      className={`py-24 sm:py-32 relative ${isDark ? 'bg-[#0a0a0a]' : 'bg-[#F1F5F9]'}`}
      ref={ref}
      data-testid="ecosystem-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-blue-500 mb-4 block">
            Ecosystem
          </span>
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Complete <span className="gradient-text">Web3 Stack</span>
          </h2>
          <p className={`text-base sm:text-lg max-w-2xl mx-auto ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
            Everything you need to participate in the decentralized economy, all in one ecosystem.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {ecosystemItems.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className={`${item.span} relative group`}
            >
              <div className={`h-full rounded-3xl overflow-hidden backdrop-blur-2xl p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
                isDark 
                  ? 'bg-white/[0.03] border border-white/10' 
                  : 'bg-white border border-black/5 shadow-sm'
              }`}>
                {/* Gradient background on hover */}
                <div
                  className={`absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500 bg-gradient-to-br ${item.gradient}`}
                />

                <div className="relative z-10">
                  {/* Icon */}
                  <div
                    className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}
                  >
                    <item.icon className="w-7 h-7 text-white" />
                  </div>

                  {/* Content */}
                  <h3 className={`text-xl sm:text-2xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {item.title}
                  </h3>
                  <p className={isDark ? 'text-zinc-400' : 'text-slate-600'}>
                    {item.description}
                  </p>
                </div>

                {/* Corner glow */}
                <div
                  className={`absolute -bottom-20 -right-20 w-40 h-40 rounded-full blur-3xl opacity-0 group-hover:opacity-30 transition-opacity duration-500 bg-gradient-to-br ${item.gradient}`}
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
